import {
	Avatar,
	Box,
	Button,
	Group,
	Text,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import classes from './SiteItem.module.css';
import {
	IconCirclePlus,
	IconQuestionMark,
} from '@tabler/icons-react';
import { getCountryName } from '@utils/countries';
import {
	getRatioLabel,
	truncateString,
} from '@utils/functions';
import { Tooltip } from '@components/atoms';
import useStore from '@store/index';

// Types
type PropertyProps = {
	label: string;
	value: string;
};

type LocationProps = {
	label: string;
	countryCode: string;
	place: string;
};

export type MeterpointData = {
	serialNumber: string;
	countryCode: string;
	place: string;
	unit: string;
};

export type ConversionFactorData = {
	startDate: string;
	unit: string;
	countryCode: string;
	place: string;
	ratio: string;
};

export type SiteProps = {
	site: {
		id: string;
		name: string;
		countryCode: string;
		place: string;
		unit: string;
	};
	meterpoints: MeterpointData[];
	conversionFactors: ConversionFactorData[];
	projectId: string | undefined;
	producerId: string | undefined;
};

// Components
const Property: React.FC<PropertyProps> = ({
	label,
	value,
}) => {
	const { t } = useTranslation();
	return (
		<Box className={classes.rowProperties}>
			<Box className={classes.containerLabel}>
				<Text className={classes.textLabel}>
					{t(label)}
				</Text>
			</Box>
			<Box className={classes.containerValue}>
				<Text className={classes.textValue}>{value}</Text>
			</Box>
		</Box>
	);
};

const Location: React.FC<LocationProps> = ({
	label,
	countryCode,
	place,
}) => {
	const { t } = useTranslation();
	return (
		<Box className={classes.rowProperties}>
			<Box className={classes.containerLabel}>
				<Text className={classes.textLabel}>
					{t(label)}
				</Text>
			</Box>
			<Tooltip
				label={`${getCountryName(
					countryCode
				)} / ${place}`}
				isActive={
					place && place.length > 7 ? true : false
				}
			>
				<Box className={classes.containerValue}>
					<Group gap={'5px'}>
						<Avatar
							className={classes.countryFlag}
							src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
							size={'18px'}
						>
							<IconQuestionMark />
						</Avatar>
						<Text className={classes.countryText}>
							{getCountryName(countryCode)}
						</Text>
						<Text className={classes.placeText}>
							{`/ ${truncateString(place, 7)}`}
						</Text>
					</Group>
				</Box>
			</Tooltip>
		</Box>
	);
};

const MeterpointList: React.FC<{
	meterpoints: MeterpointData[];
	siteId: string | undefined;
	projectId: string | undefined;
	producerId: string | undefined;
}> = ({ meterpoints, siteId, projectId, producerId }) => {
	const { t } = useTranslation();

	const {
		setOpenDrawerMeterPointForm,
		setParamsFromProject,
	} = useStore();
	return (
		<Box className={classes.containerMeterpoint}>
			{meterpoints.map((point, index) => (
				<Box key={index}>
					<Box className={classes.containerProperties}>
						{index == 0 && (
							<Box className={classes.rowProperties}>
								<Box
									className={classes.containerLabel}
								></Box>
								<Box className={classes.containerValue}>
									<Button
										style={{
											marginLeft: '-4px',
										}}
										p={'0px'}
										size="compact-xs"
										variant="transparent"
										leftSection={
											<IconCirclePlus size={23} />
										}
										onClick={() => {
											setOpenDrawerMeterPointForm(true);
											setParamsFromProject({
												project: projectId,
												producer: producerId,
												site: siteId,
											});
										}}
									>
										{t('meterPoints.addMeterPoint')}
									</Button>
								</Box>
							</Box>
						)}
						<Property
							label="global.serialNumber"
							value={point.serialNumber}
						/>
						<Location
							label="global.location"
							countryCode={point.countryCode}
							place={point.place}
						/>
						<Property
							label="global.unit"
							value={point.unit}
						/>
					</Box>
					{index < meterpoints.length - 1 && (
						<Box className={classes.withBottomBorder} />
					)}
				</Box>
			))}
		</Box>
	);
};

const ConversionFactorList: React.FC<{
	conversionFactors: ConversionFactorData[];
}> = ({ conversionFactors }) => (
	<Box className={classes.containerConversionFactor}>
		{conversionFactors.map((factor, index) => (
			<Box key={index}>
				<Box
					className={classes.containerProperties}
					key={index}
				>
					<Property
						label="global.startDate"
						value={factor.startDate}
					/>
					<Property
						label="global.value"
						value={getRatioLabel(
							Number(factor.ratio),
							factor.unit.toLocaleUpperCase()
						)}
					/>
				</Box>
				{index < conversionFactors.length - 1 && (
					<Box className={classes.withBottomBorder} />
				)}
			</Box>
		))}
	</Box>
);

export const SiteItem: React.FC<SiteProps> = ({
	site,
	meterpoints,
	conversionFactors,
	projectId,
	producerId,
}) => {
	const { t } = useTranslation();
	return (
		<Box className={classes.container}>
			<Box className={classes.containerSite}>
				<Box className={classes.containerModule}>
					<Text className={classes.textModule}></Text>
				</Box>
				<Box className={classes.containerPropertiesSite}>
					<>
						<Location
							label="global.location"
							countryCode={site.countryCode}
							place={site.place}
						/>
						<Property
							label="global.unit"
							value={site.unit}
						/>
					</>
				</Box>
			</Box>
			{conversionFactors.length > 0 && (
				<Box
					className={classes.containerConversionFactors}
				>
					<Box className={classes.containerModule}>
						<Text className={classes.textModule}>
							{t('sidebar.conversionFactors')}
						</Text>
					</Box>
					<Box className={classes.containerProperties}>
						<ConversionFactorList
							conversionFactors={conversionFactors}
						/>
					</Box>
				</Box>
			)}
			{meterpoints.length > 0 && (
				<Box className={classes.containerMeterpoints}>
					<Box className={classes.containerModule}>
						<Text className={classes.textModule}>
							{t('global.meterPoints')}
						</Text>
					</Box>

					<Box className={classes.containerProperties}>
						<MeterpointList
							siteId={site.id}
							projectId={projectId}
							producerId={producerId}
							meterpoints={meterpoints}
						/>
					</Box>
				</Box>
			)}
		</Box>
	);
};
